import React, { useEffect, useRef, useState } from "react";
import { Collapse, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isMenuItem, MenuItem, MixedMenuItem, SubMenuItem } from "../Interfaces/Interfaces";
import { useTranslation } from "react-i18next";
import UserfulTooltip from "../Widgets/UserfulTooltip";
import { faAngleDown, faAngleRight } from "@fortawesome/pro-solid-svg-icons";

import "./sidenav.scss";
import "./SideNav.scss";

interface IProps {
    show?: boolean;
    showContent: string;
    handleChangeShowContent: any;
    onAction?: () => void;
    menuItems: MixedMenuItem[];
    overrideShowContent?: string;
    noTranslate?: boolean;
}

interface OpenSubNav {
    name: string;
    open: boolean;
}

export default function SideNav({ show = true, ...props }: IProps) {
    const { t } = useTranslation();
    const [openSubNav, setOpenSubNav] = useState<OpenSubNav[]>([]);
    const { handleChangeShowContent, menuItems, onAction } = props;
    const sideNavRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!show) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (sideNavRef.current && !sideNavRef.current.contains(event.target as Node)) {
                onAction?.();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onAction, show]);

    const handleSubMenuItemClick = (name: string) => {
        // Check if name is in openSubNav
        const items = [...openSubNav];
        const resIndex = openSubNav.findIndex((item) => item.name === name);
        if (resIndex > -1) {
            items.splice(resIndex, 1, { name, open: !openSubNav[resIndex].open });
            setOpenSubNav(items);
        } else {
            items.push({ name: name, open: false });
            setOpenSubNav(items);
        }
    };

    const handleMenuItemClick = (name: string) => {
        handleChangeShowContent(name);
        onAction?.();
    };

    const isCollapseOpen = (name) => {
        const res = openSubNav.find((ele) => ele.name === name);
        if (res) return res.open;
        return true;
    };

    const renderSubMenuItem = (item: MixedMenuItem) => {
        if (!item) {
            return null;
        }
        if (isMenuItem(item)) {
            return renderMenuItem(item);
        }

        const subMenuItem = item as SubMenuItem;

        return (
            <>
                <UserfulTooltip title={(props.noTranslate ? item.tooltip : t(item.tooltip)) || ""}>
                    <button
                        className="userful-nav-item-wrapper"
                        onClick={() => handleSubMenuItemClick(subMenuItem.subMenuHeaderTitle)}
                    >
                        <div className="userful-nav-icon-wrapper">
                            <FontAwesomeIcon icon={subMenuItem.icon.iconName} />
                        </div>
                        <p>{props.noTranslate ? subMenuItem.subMenuHeaderTitle : t(subMenuItem.subMenuHeaderTitle)}</p>

                        <div className="userful-nav-icon-wrapper">
                            {isCollapseOpen(subMenuItem.subMenuHeaderTitle) ? (
                                <FontAwesomeIcon icon={faAngleDown} className="arrowIcon" />
                            ) : (
                                <FontAwesomeIcon icon={faAngleRight} className="arrowIcon" />
                            )}
                        </div>
                    </button>
                </UserfulTooltip>
                <Collapse in={isCollapseOpen(subMenuItem.subMenuHeaderTitle)}>
                    <div className="nav-items-group">
                        {subMenuItem.subMenu
                            .filter((m) => m)
                            .map((menu) => (
                                <UserfulTooltip
                                    key={menu.title}
                                    title={props.noTranslate ? menu.tooltip : t(menu.tooltip)}
                                >
                                    <button
                                        style={{ paddingLeft: "48px" }}
                                        className={`userful-nav-item-wrapper ${menu.isSelected || props.showContent === menu.title ? "userful-active-nav-item-wrapper" : ""}`}
                                        onClick={() => handleMenuItemClick(menu.title)}
                                        disabled={menu.disable}
                                    >
                                        {menu.icon && (
                                            <div className="userful-nav-icon-wrapper">
                                                <FontAwesomeIcon icon={menu.icon.iconName} />
                                            </div>
                                        )}
                                        <p>{props.noTranslate ? menu.title : t(menu.title)}</p>
                                    </button>
                                </UserfulTooltip>
                            ))}
                    </div>
                </Collapse>
            </>
        );
    };
    const renderMenuItem = (item: MixedMenuItem) => {
        if (!item) {
            return null;
        }
        if (!isMenuItem(item)) {
            return renderSubMenuItem(item);
        }

        const menuItem = item as MenuItem;

        return (
            <UserfulTooltip key={menuItem.title} title={props.noTranslate ? menuItem.tooltip : t(menuItem.tooltip)}>
                <button
                    className={`userful-nav-item-wrapper ${item.isSelected || props.showContent === menuItem.title ? "userful-active-nav-item-wrapper" : ""}`}
                    onClick={() => handleMenuItemClick(menuItem.title)}
                    disabled={menuItem.disable}
                >
                    <div className="userful-nav-icon-wrapper">
                        <FontAwesomeIcon icon={menuItem.icon.iconName} />
                    </div>
                    <p>{props.noTranslate ? menuItem.title : t(menuItem.title)}</p>
                </button>
            </UserfulTooltip>
        );
    };

    return (
        <div className={`side-nav nonselect ${show ? "side-nav-show" : "side-nav-hide"}`} ref={sideNavRef}>
            <div className="nav-items-group">
                {menuItems.filter((m) => m).map((menuItem) => renderMenuItem(menuItem))}
            </div>
        </div>
    );
}
